import React, { PropsWithChildren } from 'react';
import { NavLink } from 'react-router-dom';

import {
  Sidebar,
  SidebarPage,
  sidebarConfig,
  SidebarItem,
  SidebarDivider,
  SidebarSpace,
  useSidebarOpenState,
} from '@backstage/core-components';
import { useRouteRef } from '@backstage/core-plugin-api';
import { techdocsPlugin } from '@backstage/plugin-techdocs';
import { Settings as SidebarSettings } from '@backstage/plugin-user-settings';
import { SidebarSearchModal } from '@backstage/plugin-search';

import { Link, makeStyles } from '@material-ui/core';

import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import ExtensionIcon from '@material-ui/icons/Extension';
import HelpIcon from '@material-ui/icons/Help';
import HomeIcon from '@material-ui/icons/Home';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import MapIcon from '@material-ui/icons/MyLocation';
import PeopleIcon from '@material-ui/icons/People';
import SpeedIcon from '@material-ui/icons/Speed';
import AdbIcon from '@material-ui/icons/Adb';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';

// Custom Imports
import { AwsIcon, KingpinIcon } from '../custom-icons';
import RouterIcon from '@material-ui/icons/Router';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link
        component={NavLink}
        to="/"
        underline="none"
        className={classes.link}
        aria-label="Home"
      >
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

export const Root = ({ children }: PropsWithChildren<{}>) => {
  const techdocsRoute = useRouteRef(techdocsPlugin.routes.docRoot);
  const backstageHelp = techdocsRoute({
    kind: 'component',
    name: 'backstage',
    namespace: 'default',
  });

  return (
    <SidebarPage>
      <Sidebar>
        <SidebarLogo />
        <SidebarSearchModal />
        <SidebarItem icon={HelpIcon} to={backstageHelp} text="Help" />
        <SidebarDivider />
        {/* Global nav */}
        <SidebarItem icon={HomeIcon} to="catalog" text="Home" />
        <SidebarItem icon={ExtensionIcon} to="api-docs" text="APIs" />
        <SidebarItem icon={LibraryBooks} to="docs" text="Docs" />
        <SidebarItem icon={CreateComponentIcon} to="create" text="Create..." />
        {/* End global nav */}
        <SidebarDivider />
        <SidebarItem icon={MapIcon} to="tech-radar" text="Tech Radar" />
        <SidebarItem icon={PeopleIcon} to="engagement" text="Engagement" />
        <SidebarItem icon={AwsIcon} to="aws-dashboard" text="AWS Dashboard" />
        <SidebarItem icon={KingpinIcon} to="kingpin-ui" text="FDM" />
        <SidebarItem icon={AdbIcon} to="kingpin-ui/oncall" text="On-Call" />
        <SidebarItem
          icon={RouterIcon}
          to="router-visualizer"
          text="Router Vis"
        />
        <SidebarItem icon={SpeedIcon} to="apf" text="APF" />
        <SidebarSpace />
        <SidebarDivider />
        <SidebarSettings />
      </Sidebar>
      {children}
    </SidebarPage>
  );
};
